import React, { useState } from 'react';
import { Autocomplete, DialogContent, DialogTitle, Grid, TextField, Typography, Dialog, DialogActions, Button, DialogContentText } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import FancyDialogActions from "./FancyDialogActions";
import { getDatesInRange, formatDateRange, formatNumber } from "./formatters";
import { es } from 'date-fns/locale';
import { format, parse } from 'date-fns';
import { useApp } from '../../hooks/useApp';
import {
    updateAllWeeklyPayrollPayments,
    validateHours
} from '../../services/advances/advancesService';
import dayjs from 'dayjs';
import {CoworkerWorkedHoursRequest} from "../../types/project-progress/types";
import { getVacationDays } from '../../services/vacaciones/VacacionesService';

interface DialogProps {
    actionButton?: any;
    data: any;
    cancelModal: any;
    getData?: any;
    coworkersData?: any;
}

const AddOrUpdateCoworkerModal = ({ data, cancelModal, getData, coworkersData }: DialogProps) => {
    const { setLoading, setErrorMsg, setSuccessMsg, authInfo } = useApp();
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [validationMessage, setValidationMessage] = useState("");
    const [pendingFormData, setPendingFormData] = useState<any>(null);

    const isEdit = data?.isEdit || false;
    const editData = data?.editData;

    const [startDate, endDate] = data.modalObject.modalObject.split(' / ');
    const datesInRange = getDatesInRange(startDate, endDate);

    const validation = Yup.object().shape({
        dailyHours: Yup.object().shape(
            datesInRange.reduce((acc: any, date: any) => {
                acc[date] = Yup.number()
                    .typeError("Debe ser un número")
                    .min(0, "Debe ser mayor o igual a 0")
                    .required("Campo requerido");
                return acc;
            }, {})
        ),
        selectedUser: Yup.object().nullable().required("Debe seleccionar un usuario")
    });

    // Función para extraer las horas por día desde los datos de edición
    const getEditHours = () => {
        if (!isEdit || !editData) return {};

        return {
            [datesInRange[0]]: editData.thursdayWorkedHours || 0,
            [datesInRange[1]]: editData.fridayWorkedHours || 0,
            [datesInRange[2]]: editData.saturdayWorkedHours || 0,
            [datesInRange[3]]: editData.sundayWorkedHours || 0,
            [datesInRange[4]]: editData.mondayWorkedHours || 0,
            [datesInRange[5]]: editData.tuesdayWorkedHours || 0,
            [datesInRange[6]]: editData.wednesdayWorkedHours || 0,
        };
    };

    // Función para encontrar el usuario seleccionado en modo edición
    const getEditUser = () => {
        if (!isEdit || !editData || !coworkersData) {
            return null;
        }

        // Buscar por nombre y positionId para la coincidencia exacta
        const foundWorker = coworkersData.find((worker: any) => {
            return worker.name === editData.name && worker.positionId === editData.positionId;
        });
        return foundWorker || null;
    };

    const defaultValues = {
        dailyHours: isEdit ? getEditHours() : datesInRange.reduce((acc: any, date: any) => {
            acc[date] = 0;
            return acc;
        }, {}),
        selectedUser: isEdit ? getEditUser() : null
    };

    const { control, handleSubmit, watch, formState: { errors }, setValue } = useForm({
        defaultValues,
        //@ts-ignore
        resolver: yupResolver(validation),
    });

    // Establecer los valores iniciales cuando el componente se monta
    React.useEffect(() => {

        if (isEdit && editData && coworkersData) {
            const editUser = getEditUser();
            if (editUser) {
                setValue('selectedUser', editUser);
            }
        }
    }, [isEdit, editData, coworkersData, setValue]);

    const watchAllFields = watch();
    const selectedUserId = watchAllFields.selectedUser?.id;

    // Días de la semana en que el colaborador está de vacaciones. Esos días no
    // admiten horas de trabajo: ya se pagan como jornada completa en el
    // proyecto "Vacaciones", así que cargarlos acá los pagaría dos veces. El
    // backend rechaza el guardado igual; esto es para que el usuario lo vea
    // antes de intentarlo.
    //
    // No se les toca el valor: en un proyecto normal ya vienen en cero, y en el
    // avance del propio proyecto "Vacaciones" vienen con la jornada cargada por
    // el sistema. En los dos casos lo correcto es dejarlo como está.
    const [vacationDates, setVacationDates] = useState<string[]>([]);

    React.useEffect(() => {
        if (!selectedUserId) {
            setVacationDates([]);
            return;
        }
        let active = true;
        (async () => {
            const days = await getVacationDays(
                selectedUserId,
                datesInRange[0],
                datesInRange[datesInRange.length - 1]
            );
            if (!active) return;
            setVacationDates(days);
        })();
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedUserId]);

    const totalHours = datesInRange.reduce(
        (total, date) => total + Number(watchAllFields.dailyHours[date] || 0),
        0
    );

    const validateHoursBeforeSubmit = async (formData: any, selectedUser: any, year: number, week: number) => {
        const payload = {
            personId: selectedUser.id,
            year,
            week,
            thursdayHours: formData.dailyHours[datesInRange[0]] || 0, // Jueves
            fridayHours: formData.dailyHours[datesInRange[1]] || 0,   // Viernes
            saturdayHours: formData.dailyHours[datesInRange[2]] || 0, // Sábado
            sundayHours: formData.dailyHours[datesInRange[3]] || 0,   // Domingo
            mondayHours: formData.dailyHours[datesInRange[4]] || 0,   // Lunes
            tuesdayHours: formData.dailyHours[datesInRange[5]] || 0,  // Martes
            wednesdayHours: formData.dailyHours[datesInRange[6]] || 0, // Miércoles
        };

        const response = await validateHours(payload);
        return response;
    };

    const submitFormData = async (formData: any) => {
        try {
            setLoading(true);

            const dailyHoursEntries = Object.entries(formData.dailyHours).reverse();
            const requestArray: CoworkerWorkedHoursRequest[] = dailyHoursEntries.map(
                ([date, hours]) => ({
                    date: dayjs(date).format("DD-MM-YYYY"),
                    positionId: formData.selectedUser.positionId,
                    personId: formData.selectedUser.id,
                    progressId: data?.advanceData?.id ?? data?.advanceId,
                    workedHours: Number(hours),
                    user: authInfo?.username, // Agregar el parámetro user con el username
                })
            );
            let projectProgress = await updateAllWeeklyPayrollPayments(requestArray);
            if(!projectProgress){
                throw new Error(`Error procesando las horas del colaborador`);
            }
            const successMessage = isEdit ? "Actualización de horas exitosa" : "Contabilización de horas exitosa";
            setSuccessMsg(successMessage);
        } catch (error: any) {
            setErrorMsg(error);
        } finally {
            setLoading(false);
        }

        cancelModal();
        await getData();
    };

    const updateWorkedHours = async (formData: any) => {
        try {
             await submitFormData(formData);
        } catch (error) {
            setErrorMsg("Error al validar las horas.");
        }
    };

    const runSubmitFlow = async (formData: any) => {
        try {
            const validationResponse = await validateHoursBeforeSubmit(
                formData,
                formData.selectedUser,
                data?.year,
                data?.week
            );

            const hasExceededHours = Object.values(validationResponse).length > 0;

            if (hasExceededHours) {
                setValidationMessage(
    "Alerta: La suma de horas registradas por día en todos los proyectos asignados al colaborador durante la semana actual excede el mínimo diario establecido. Ya sea en este proyecto u otros que tenga asignados el colaborador"
                );
                setPendingFormData(formData);
                setShowConfirmationModal(true);
            } else {
                await submitFormData(formData);
            }
        } catch (error) {
            setErrorMsg("Error al validar las horas.");
        }
    };
    const onSubmit = async (formData: any) => {
        // Verificar usuarios con 2 o mas puestos asignados 
        if (!isEdit) {
            const countOccurrences = coworkersData?.filter(
                (worker: any) => worker.id === formData.selectedUser?.id
            ).length || 0;

            if (countOccurrences >= 2) {
                setValidationMessage(
                    "Alerta: El colaborador seleccionado tiene 2 puestos asignados. ¿Confirma que la información es correcta?"
                );
                setPendingFormData(formData);
                setShowConfirmationModal(true);
                return;
            }
        }
        runSubmitFlow(formData);
    };

    const handleConfirm = async () => {
        setShowConfirmationModal(false);
        await updateWorkedHours(pendingFormData);
        setPendingFormData(null);
    };

    const handleCancel = () => {
        setShowConfirmationModal(false);
    };

    return (
        <>
            <DialogTitle sx={{ pt: 4, px: 4 }}>
                {isEdit ? "Editar Horas por día" : "Horas por día"}
            </DialogTitle>
            <DialogContent>
                <Typography variant="subtitle1" sx={{ mb: 2, pt: 2, px: 4 }}>
                    Rango de fechas: {formatDateRange(data.modalObject.modalObject)}
                </Typography>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={2} direction="column" sx={{ px: 4 }}>
                        <Grid item xs={12}>
                            <Controller
                                name="selectedUser"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <Autocomplete
                                        options={coworkersData || []}
                                        getOptionLabel={(option: any) =>
                                            `${option.name} - ${option.positionName} - ${formatNumber(option.salaryPerHour)}`
                                        }
                                        value={value || null}
                                        onChange={(_, newValue) => onChange(newValue)}
                                        disabled={isEdit} // Deshabilitar en modo edición
                                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Seleccionar usuario"
                                                error={!!errors.selectedUser}
                                                helperText={errors.selectedUser?.message as string}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>
                        {datesInRange.map((date) => {
                            const onVacation = vacationDates.includes(date);
                            return (
                            <Grid item xs={12} key={date} sx={{ mt: 2 }}>
                                <Controller
                                    name={`dailyHours.${date}`}
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={`${format(parse(date, 'yyyy-MM-dd', new Date()), "EEEE, d 'de' MMMM", { locale: es })} (${date})`}
                                            type="number"
                                            inputProps={{ min: 0, step: 0.25 }}
                                            disabled={onVacation}
                                            helperText={onVacation
                                                ? "Vacaciones: no se pueden registrar horas este día"
                                                : undefined}
                                            {...field}
                                        />
                                    )}
                                />
                            </Grid>
                            );
                        })}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mt: 2 }}>
                                Total: {totalHours} horas
                            </Typography>
                        </Grid>
                    </Grid>
                    <FancyDialogActions
                        handleAccept={handleSubmit(onSubmit)}
                        cancelModal={cancelModal}
                        textAcceptButton={isEdit ? "Actualizar" : "Guardar"}
                    />

                </form>
            </DialogContent>

            <Dialog open={showConfirmationModal} onClose={handleCancel}>
                <DialogContent>
                    <Typography>{validationMessage}</Typography>
                </DialogContent>
                <FancyDialogActions
                    handleAccept={handleConfirm}
                    cancelModal={handleCancel}
                    textAcceptButton="Aceptar"
                    textCancelButton="Cancelar"
                />
            </Dialog>
        </>
    );
};

export default AddOrUpdateCoworkerModal;